import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useParams } from 'react-router-dom';

import { Box, Stack, Typography } from '@mui/material';
import Grid from '@mui/system/Grid';
import useIntegration from 'api/useIntegration';
import usePublisher from 'api/usePublisher';
import useUsers from 'api/useUsers';
import {
  EChecklistTaskStatus,
  EPublisherSolutionModelSubType,
  ETaskId
} from 'common/contracts';
import { PROFILES_ENABLED_PLACEHOLDER } from 'constants/constants';
import { EFeatureFlag } from 'constants/enums';
import { useFormik } from 'formik';
import { useProfilesManager } from 'hooks/useProfilesManager';
import { ERouteContext } from 'routes/types';
import { useChecklistState } from 'state/hooks/setup-checklist.hook';

import AcCard from 'components/AcCard/AcCard';
import AcInput from 'components/AcInput/AcInput';
import ActionButton from 'components/ActionButton/ActionButton';
import { cn } from 'components/lib/utils';
import CustomizedSwitch from 'components/SwitchButton/SwitchButton';

import { EToasterStatus, showToast } from '../../../utils/showToast';
import { IntegrationSettingsData } from '../Settings.types';

import AwardingFields from './AwardingFields';
import {
  INTEGRATION_SETTINGS_FIELDS,
  PAYMENT_LINKS_FIELDS
} from './integrationSettingsConstants';
import {
  createWebhooksSchema,
  fieldGroups,
  getInitialValues,
  integrationFields,
  IntegrationFormValues
} from './integrationSettingsSchema';

import 'style/forms.scss';

export enum EEncryptionType {
  SYMMETRIC_ENCRYPTION = 'SymmetricEncryption',
  SIGNATURE_HASHING = 'SignatureHashing'
}

const IntegrationSettings = ({ context }: { context: ERouteContext }) => {
  const { id } = useParams<{ id: string }>();
  const { toggleIntegrationProfiles, isProfilesEnabled, isToggleInProgress } =
    useProfilesManager();
  const { getIntegration, updateIntegration }: any = useIntegration(
    id as string
  );
  const { fetchFeatureFlags } = useUsers({ enableFeatureFlags: false });
  const { getPublisher } = usePublisher(id || undefined);
  const data = getIntegration?.data;
  const integrationRefetch = getIntegration?.refetch;
  const isProfilesFeatureFlagOn = useMemo(
    () => fetchFeatureFlags?.data?.featureFlags?.integration_profiles,
    [fetchFeatureFlags?.data?.featureFlags?.integration_profiles]
  );
  const { hasCategories, setTaskStatus, isWebStore, isWebCheckout } =
    useChecklistState();
  const isAppsflyerIntegrationFeatureFlagOn =
    fetchFeatureFlags?.data?.featureFlags?.dashboard_appsflyer;
  const isEventsV2FeatureFlagOn =
    fetchFeatureFlags.data?.featureFlags?.[EFeatureFlag.DASHBOARD_EVENTS_V2];

  const isPaymentLinkProject =
    getPublisher?.data?.solutionModelSubtype ===
    EPublisherSolutionModelSubType.PAYMENT_LINKS;

  const getFieldGroups = useCallback(() => {
    if (isPaymentLinkProject) {
      return [...PAYMENT_LINKS_FIELDS];
    }

    let basicFields = [...fieldGroups.basic];

    if (fetchFeatureFlags.data?.featureFlags.Dashboard_awarding_v2_rollout) {
      basicFields = basicFields.filter((field) => {
        return ![
          INTEGRATION_SETTINGS_FIELDS.AWARDING_V2_API_URL,
          INTEGRATION_SETTINGS_FIELDS.ORDERS_REPORTING_API_URL
        ].includes(field as any);
      });
    } else {
      basicFields = basicFields.filter((field) => {
        return field !== INTEGRATION_SETTINGS_FIELDS.AWARDING_V2_API_URL;
      });
    }

    // If it's a portal, exclude EVENTS_WEBHOOK_URL from basic fields
    if (context === ERouteContext.PORTAL) {
      basicFields = basicFields.filter(
        (field) => field !== INTEGRATION_SETTINGS_FIELDS.EVENTS_WEBHOOK_URL
      );
    }

    return [
      ...basicFields,
      ...(isAppsflyerIntegrationFeatureFlagOn ? fieldGroups.appsFlyer : []),
      ...fieldGroups.deeplink
    ];
  }, [
    isPaymentLinkProject,
    isAppsflyerIntegrationFeatureFlagOn,
    context,
    fetchFeatureFlags.data?.featureFlags.Dashboard_awarding_v2_rollout
  ]);

  useEffect(() => {
    return () => {
      integrationRefetch();
    };
  }, [integrationRefetch]);

  const updateIntegrationTasks = (
    webhookValues: Partial<IntegrationSettingsData>,
    initialValues: Partial<IntegrationSettingsData>,
    isWebStore: boolean,
    setTaskStatus: (taskId: ETaskId, status?: EChecklistTaskStatus) => void
  ): void => {
    const webhookToTaskMap: Record<string, ETaskId> = {
      ordersReportingApiUrl: ETaskId.SETUP_AWARD_API,
      ...(isWebStore && {
        playerInfoSyncUrl: ETaskId.SETUP_PERSONALIZATION_API,
        playersAuthWebhook: ETaskId.CUSTOMIZE_AUTH_API
      })
    };

    Object.entries(webhookToTaskMap).forEach(([webhookField, taskId]) => {
      const currentValue =
        webhookValues[webhookField as keyof typeof webhookValues];
      const initialValue =
        initialValues[webhookField as keyof typeof initialValues];

      const currentValueTrimmed = currentValue?.trim() || '';
      const initialValueTrimmed = initialValue?.trim() || '';

      const hasWebhookValueChanged =
        currentValueTrimmed !== initialValueTrimmed;

      if (hasWebhookValueChanged) {
        setTaskStatus(
          taskId,
          currentValueTrimmed.length === 0
            ? EChecklistTaskStatus.OPEN
            : EChecklistTaskStatus.DONE
        );
      }
    });
  };

  const saveWebhooksDetails = (
    webhookValues: Partial<IntegrationSettingsData>,
    initialValues: Partial<IntegrationSettingsData>
  ) => {
    updateIntegration.mutate(webhookValues, {
      onSuccess: () => {
        getPublisher.refetch();
        showToast({
          message: 'Webhook details updated successfully',
          status: EToasterStatus.SUCCESS
        });
        if (hasCategories && (isWebStore || isWebCheckout)) {
          updateIntegrationTasks(
            webhookValues,
            initialValues,
            isWebStore,
            setTaskStatus
          );
        }
      },
      onError: (data: any) => {
        showToast({
          message: 'Failed to update Webhook details',
          status: EToasterStatus.ERROR
        });
      }
    });
  };

  const initialValues = useMemo(
    () =>
      getInitialValues(
        getFieldGroups(),
        data,
        fetchFeatureFlags.data?.featureFlags.Dashboard_awarding_v2_rollout
      ),
    [
      data,
      fetchFeatureFlags.data?.featureFlags.Dashboard_awarding_v2_rollout,
      getFieldGroups
    ]
  );

  const webhooksForm = useFormik<IntegrationFormValues>({
    initialValues,
    validationSchema: createWebhooksSchema(getFieldGroups()),
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (values) {
        const {
          appsFlyerIntegrationOn,
          appsFlyerS2SToken,
          appsFlyerApplicationID,
          ...rest
        } = values;
        const webhookDetailsData = {
          appsFlyerIntegration: isAppsflyerIntegrationFeatureFlagOn
            ? {
                appsFlyerIntegrationOn,
                appsFlyerS2SToken,
                appsFlyerApplicationID
              }
            : undefined,
          ...rest
        } as Partial<IntegrationSettingsData>;
        saveWebhooksDetails(webhookDetailsData, initialValues);
      }
    }
  });

  const {
    values,
    touched,
    errors,
    setFieldTouched,
    handleChange,
    handleBlur,
    setFieldValue,
    submitForm
  } = webhooksForm;

  const prevValuesRef = useRef<IntegrationFormValues>(values);

  const onToggleJwtSecretHandler = useCallback(() => {
    const currentValue = values.deeplinkOn as boolean;
    setFieldValue('deeplinkOn', !currentValue, true).then(() => {
      submitForm();
    });
  }, [values.deeplinkOn, setFieldValue, submitForm]);

  useEffect(() => {
    Object.keys(values).forEach((key) => {
      if (values[key] !== prevValuesRef.current[key]) {
        if (!touched[key]) {
          setFieldTouched(key, true);
        }
      }
    });

    prevValuesRef.current = values;
  }, [values, touched, setFieldTouched]);

  const toggleIntegrationProfilesHandler = () => {
    toggleIntegrationProfiles(!isProfilesEnabled);
  };

  const renderField = useCallback(
    (fieldName: string) => {
      const field = integrationFields[fieldName];
      if (!field) return null;

      if (field.type === 'boolean') {
        return null;
      }

      if (
        fieldName === INTEGRATION_SETTINGS_FIELDS.EVENTS_V2_WEBHOOK_URL &&
        !isEventsV2FeatureFlagOn &&
        context !== ERouteContext.PORTAL
      ) {
        return null;
      }

      return (
        <Grid size={12} key={fieldName}>
          <AcInput
            header={field.label}
            name={fieldName}
            value={
              isProfilesEnabled && field.type === 'text'
                ? PROFILES_ENABLED_PLACEHOLDER
                : (values[fieldName] as string) || ''
            }
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isProfilesEnabled || field.disabled}
            error={touched[fieldName] && Boolean(errors[fieldName])}
            helperText={touched[fieldName] ? errors[fieldName]?.toString() : ''}
            type={field.type}
            enableCopy={field.enableCopy}
            placeholder={field.placeholder}
            required={field.required}
            customClass={field.name.includes('Url') ? 'url-input' : ''}
          />
        </Grid>
      );
    },
    [
      values,
      touched,
      errors,
      isProfilesEnabled,
      handleChange,
      handleBlur,
      context,
      isEventsV2FeatureFlagOn
    ]
  );

  return (
    <Grid container className="formContent">
      <Grid size={5} className="formContent-minWIdth">
        <Stack>
          <AcCard stackContainer={false} title="Integration links">
            {isProfilesFeatureFlagOn && (
              <Stack direction="row" alignItems="center" mb={2}>
                <Typography variant="body2" marginRight={'16px'}>
                  Enable Integration Environments
                </Typography>
                <CustomizedSwitch
                  status={isProfilesEnabled}
                  texts={[]}
                  disabled={isToggleInProgress}
                  functions={[
                    toggleIntegrationProfilesHandler,
                    toggleIntegrationProfilesHandler
                  ]}
                />
              </Stack>
            )}
            <Grid container rowSpacing={2} columnSpacing={1.5}>
              {fetchFeatureFlags.data?.featureFlags
                .Dashboard_awarding_v2_rollout && (
                <AwardingFields
                  values={values}
                  setFieldValue={setFieldValue}
                  errors={errors}
                />
              )}
              {getFieldGroups().map(renderField)}
              {!isPaymentLinkProject && (
                <Grid size={12}>
                  <AcCard title="Deeplink" className="mt-2">
                    <Stack direction="row" alignItems="center" mb={2}>
                      <Typography variant="body2" marginRight={'16px'}>
                        Enable JWT Secret
                      </Typography>
                      <CustomizedSwitch
                        status={!!values.deeplinkOn}
                        texts={[]}
                        disabled={isToggleInProgress}
                        functions={[
                          onToggleJwtSecretHandler,
                          onToggleJwtSecretHandler
                        ]}
                      />
                    </Stack>
                    {values.deeplinkOn && (
                      <AcInput
                        header="Deeplink JWT Secret"
                        name="deeplinkJwtSecret"
                        value={values.deeplinkJwtSecret}
                        type="password"
                        onBlur={handleBlur}
                        enableCopy
                        disabled
                      />
                    )}
                  </AcCard>
                </Grid>
              )}
            </Grid>
          </AcCard>
          {isAppsflyerIntegrationFeatureFlagOn && (
            <AcCard
              className="mt-6"
              stackContainer={false}
              title="AppsFlyer Integration"
            >
              <Box className="flex items-center">
                <p className={cn('mr-4', 'text-p-xs')}>
                  Enable AppsFlyer Integration
                </p>
                <CustomizedSwitch
                  status={!!values.appsFlyerIntegrationOn}
                  texts={[]}
                  functions={[
                    () => {
                      setFieldValue('appsFlyerIntegrationOn', false);
                      setFieldValue('appsFlyerS2SToken', '');
                      setFieldValue('appsFlyerApplicationID', '');
                    },
                    () => setFieldValue('appsFlyerIntegrationOn', true)
                  ]}
                />
              </Box>
              {fieldGroups.appsFlyer
                .filter((fieldName) => fieldName !== 'appsFlyerIntegrationOn')
                .map(renderField)}
            </AcCard>
          )}
        </Stack>
        <Grid my={3} size={6}>
          <ActionButton
            variant="outlined"
            text="Update"
            onClick={submitForm}
            disabled={
              !webhooksForm.isValid ||
              !webhooksForm.dirty ||
              webhooksForm.isSubmitting
            }
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
export default IntegrationSettings;
